import * as React from 'react';
import * as Redux from 'redux';
import { Option, useFormApi, useFormState } from "informed";

import { User, Query, Permission, DataItemImportance, Language } from '../../misc/api/apiTypes';
import { Dropdown } from '../../forms/informedv2/Dropdown';
import { ConfigFieldTextBox } from '../../forms/informedv2/ConfigFieldTextBox';
import { ConfigFieldTextBoxStaticPrefix } from '../../forms/informedv2/ConfigFieldTextBoxStaticPrefix';
import { ConfigFieldDropdown } from '../../forms/informedv2/ConfigFieldDropdown';
import { ConfigFieldDatePicker } from '../../forms/informedv2/ConfigFieldDatePicker';
import { ConfigFieldTextBoxWithTooltip } from '../../forms/informedv2/ConfigFieldTextBoxWithTooltip';
import { MaxLength } from '../../forms/validation/MaxLength';
import { MainState } from '../../Core/reducers';
import { HasPermission } from '../../misc/components/Authorization';
import { PermissionEnum } from '../../misc/api/ApiPermissionEnum';
import { Required } from '../../forms/validation/Required';
import LoadingWithMessage from '../../misc/components/LoadingWithMessage';
import { useCurrentStudyConfigurationContext } from '../../Core/CurrentStudyConfigurationContext';
import { useReferenceContextProvider } from '../../Core/ReferenceContextProvider';
import { SubjectFormModel } from '../SubjectsShared';
import { ConfigurableDataItemIds } from '../../misc/api/ApiConfigurableDataItems';
import { useWebPortalApiClient, useWebPortalLegacyClient } from '../../misc/api/WebPortalTokenClient';
import { Site, SubjectType } from 'webportalclient';
import { useSecurityContext } from '../../Core/SecurityContext';

interface SubjectFormBodyProps {
    isEdit: boolean;
}

export const SubjectFormBody = (props: SubjectFormBodyProps): JSX.Element => {
    const {
        isEdit,
    } = props;

    const [subjectTypes, setSubjectTypes] = React.useState<SubjectType[]>([]);
    const [sites, setSites] = React.useState<Site[]>([]);
    const [loaded, setLoaded] = React.useState(false);
    const [sitePrefix, setSitePrefix] = React.useState<string>();
    const legacyClient = useWebPortalLegacyClient();
    const client = useWebPortalApiClient();

    const formApi = useFormApi<SubjectFormModel>();
    const formState = useFormState<SubjectFormModel>();

    const { genders, smokingStatuses } = useReferenceContextProvider();
    const { languages, study, dataItemConfigurationCollection, populationGroups, studySubjectSetting } = useCurrentStudyConfigurationContext();
    const { currentUser, currentUserPermissions } = useSecurityContext();
    const [subjectLimitedLanguages, setSubjectLimitedLanguages] = React.useState<Language[]>([]);
    React.useEffect(() => {
        const loadInitial = async () => {
            const sitesResult = await client.client.getSites(undefined, `studyId eq ${study.id}`);
            setSites(sitesResult.value);

            const subjectTypesResult = await client.client.getSubjectTypes();
            setSubjectTypes(subjectTypesResult.value);


            setLoaded(true);
        }

        loadInitial();
    }, [currentUser, currentUserPermissions, study.id]);

    React.useEffect(() => {
        if (sites.length > 0 && dataItemConfigurationCollection.find(x => x.configurableDataItemId === ConfigurableDataItemIds.SubjectId).prefixWithSiteNumber) {
            const site = sites.find(s => s.id === formState.values.SiteId);
            if (site) {
                const prefixDelimiter = dataItemConfigurationCollection.find(x => x.configurableDataItemId === ConfigurableDataItemIds.SubjectId).prefixDelimiter;

                setSitePrefix(site.number + prefixDelimiter);
                if (formApi.getValue('SubjectNumber')) {
                    formApi.setValue('SubjectNumber', (formApi.getValue('SubjectNumber') as string).replace(site.number + prefixDelimiter, ""));
                }
            }
        }
    }, [formState.values.SiteId]);

    React.useEffect(() => {
        const site = sites.find(s => s.id === formState.values.SiteId);
        if (site) {
            if (studySubjectSetting?.limitSubjectLanguageSelectionToSiteCountry) {
                setSubjectLimitedLanguages(languages.filter(x => x.Id.endsWith(site.country)))
                if (subjectLimitedLanguages.length != 0 && !subjectLimitedLanguages.some(x => x.Id == formState.values.LanguageId))
                    formApi.setValue('LanguageId', undefined);
            } else {

                setSubjectLimitedLanguages(languages)
            }
        } else {
            if (studySubjectSetting?.limitSubjectLanguageSelectionToSiteCountry) {
                setSubjectLimitedLanguages([])
            } else {

                setSubjectLimitedLanguages(languages)
            }
        }
    }, [sites, formState.values.SiteId])

    const onHeightWeightBlur = async () => {
        const height = formApi.getValue('Height') as number;
        const weight = formApi.getValue('Weight') as number;
        if (height && weight)
            legacyClient.getCalculateBmi(height, weight).then(result => formApi.setValue('BMI', result));
    };

    const onDateOfBirthBlur = async (dateOfBirth: string) => {
        if (dateOfBirth) {
            legacyClient.getCalculateAge(dateOfBirth).then(result => formApi.setValue('Age', result));
        }
    };

    if (!loaded
        || dataItemConfigurationCollection.length === 0 || !studySubjectSetting
    ) return <LoadingWithMessage message="Loading Subject Form..." />;

    const onSiteChange = () => {
        const siteId = formApi.getValue("SiteId");
        const site = sites.find(s => s.id === siteId);
        if (site) {
            setSitePrefix(site ? site.number : "");
            if (studySubjectSetting?.limitSubjectLanguageSelectionToSiteCountry) {
                setSubjectLimitedLanguages(languages.filter(x => x.Id.endsWith(site.country)))
                if (!subjectLimitedLanguages.some(x => x.Id == formState.values.LanguageId))
                    formApi.setValue('LanguageId', undefined);
            }
        } else {
            if (studySubjectSetting?.limitSubjectLanguageSelectionToSiteCountry) {
                setSubjectLimitedLanguages([])
            } else {

                setSubjectLimitedLanguages(languages)
            }
        }
    }


    return (
        <>
            <div className="row">
                <div className="form-group col-md-4 col-sm-4 col-xs-12">
                    <label htmlFor="select-site">Site</label>
                    <Dropdown
                        id="SiteId"
                        field="SiteId"
                        validate={Required}
                        onChange={() => onSiteChange()}
                    >
                        <Option
                            key={"OptionSiteBlank"}
                            value={[]}
                        />
                        {sites.map(so => (
                            <Option
                                key={`Option${so.id}`}
                                value={so.id}
                            >
                                {so.number}
                            </Option>
                        ))}
                    </Dropdown>
                </div>
                {
                    dataItemConfigurationCollection.find(x => x.configurableDataItemId === ConfigurableDataItemIds.SubjectId).prefixWithSiteNumber ?
                        <ConfigFieldTextBoxStaticPrefix
                            id="SubjectNumber"
                            field="SubjectNumber"
                            className="form-group col-md-4 col-sm-4 col-xs-12"
                            siteNumber={sitePrefix}
                            validated={true}
                            required={true}
                            disabled={isEdit}
                            autoComplete="nickname"
                            itemId={ConfigurableDataItemIds.SubjectId}
                        />
                        : <ConfigFieldTextBox
                            id="SubjectNumber"
                            field="SubjectNumber"
                            className="form-group col-md-4 col-sm-4 col-xs-12"
                            validated={true}
                            required={true}
                            disabled={isEdit}
                            itemId={ConfigurableDataItemIds.SubjectId}
                        />
                }
            </div>
            <div className="row">
                <ConfigFieldDropdown
                    id="Gender"
                    field="GenderId"
                    className="form-group col-md-4 col-sm-4 col-xs-12"
                    required={true}
                    itemId={ConfigurableDataItemIds.SubjectGender}
                >
                    {
                        genders.map(so => (
                            <Option
                                key={`Option${so.Id}`}
                                value={so.Id}
                            >
                                {so.Name}
                            </Option>
                        ))
                    }
                </ConfigFieldDropdown>
                <ConfigFieldDatePicker
                    id="BirthDate"
                    field="BirthDate"
                    className="form-group col-md-4 col-sm-4 col-xs-12"
                    required={true}
                    onBlur={onDateOfBirthBlur}
                    itemId={ConfigurableDataItemIds.SubjectDateOfBirth}
                    placeholder={!isEdit}
                />
                <ConfigFieldTextBox
                    id="Age"
                    field="Age"
                    className="form-group col-md-4 col-sm-4 col-xs-12"
                    validated={true}
                    required={true}
                    autoComplete="age"
                    type="number"
                    itemId={ConfigurableDataItemIds.SubjectAge}
                />
            </div>
            <div className="row">
                <ConfigFieldDropdown
                    id="PopulationGroupName"
                    field="PopulationGroupId"
                    className="form-group col-md-4 col-sm-4 col-xs-12"
                    required={true}
                    itemId={ConfigurableDataItemIds.SubjectPopulationGroup}
                >
                    {populationGroups.map(pg => (<Option key={`Option${pg.Id}`} value={pg.Id}>{pg.Name}</Option>))}
                </ConfigFieldDropdown>
                <ConfigFieldTextBoxWithTooltip
                    id="SelfAssessedPopulationGroup"
                    field="Race"
                    labelName="Self Assessed Population Group"
                    tooltipDescription="The subject's self defined population group using the terminology that they prefer to use, this is free text and has no bearing on predicted values"
                    className="form-group col-md-4 col-sm-4 col-xs-12"
                    validated={true}
                    validate={MaxLength(40)}
                    required={false}
                    itemId={ConfigurableDataItemIds.SubjectPopulationGroup}
                />
            </div>
            <div className="row">
                <ConfigFieldDropdown
                    id="SubjectType"
                    field="SubjectTypeId"
                    className="form-group col-md-4 col-sm-4 col-xs-12"
                    required={false}
                    itemId={ConfigurableDataItemIds.SubjectType}
                    showBlankOption={false}
                >
                    {subjectTypes.map(so => (
                        <Option
                            key={`Option${so.id}`}
                            value={so.id}
                        >
                            {so.name}
                        </Option>
                    ))}
                </ConfigFieldDropdown>
                <ConfigFieldDropdown
                    id="Language"
                    field="LanguageId"
                    className="form-group col-md-4 col-sm-4 col-xs-12"
                    required={true}
                    itemId={ConfigurableDataItemIds.SubjectLanguage}
                >
                    {
                        subjectLimitedLanguages.map(so => (
                            <Option
                                key={`Option${so.Id}`}
                                value={so.Id}
                            >
                                {so.Name}
                            </Option>
                        ))}
                </ConfigFieldDropdown>
                <ConfigFieldDropdown
                    id="Smoking-Statuses"
                    field="SmokerTypeId"
                    className="form-group col-md-4 col-sm-4 col-xs-12"
                    required={true}
                    itemId={ConfigurableDataItemIds.SubjectSmokerType}
                >
                    {smokingStatuses.map(so => (
                        <Option
                            key={`Option${so.Id}`}
                            value={so.Id}
                        >
                            {so.Name}
                        </Option>
                    ))}
                </ConfigFieldDropdown>
            </div>
            <div className="row">
                <ConfigFieldTextBox
                    id="Height"
                    field="Height"
                    type="number"
                    className="form-group col-md-4 col-sm-4 col-xs-12"
                    validated={true}
                    required={true}
                    itemId={ConfigurableDataItemIds.SubjectHeight}
                    onBlur={onHeightWeightBlur}
                    isInputAddon={true}
                />
                <ConfigFieldTextBox
                    id="Weight"
                    field="Weight"
                    type="number"
                    className="form-group col-md-4 col-sm-4 col-xs-12"
                    required={true}
                    validated={true}
                    itemId={ConfigurableDataItemIds.SubjectWeight}
                    onBlur={onHeightWeightBlur}
                    isInputAddon={true}
                />
                <ConfigFieldTextBox
                    id="BMI"
                    field="BMI"
                    type="number"
                    className="form-group col-md-4 col-sm-4 col-xs-12"
                    disabled={true}
                    validated={false}
                    itemId={ConfigurableDataItemIds.SubjectBmi}
                    inputAddon={<>kg/m<sup>2</sup></>}
                />
            </div>
        </>
    );
};
